import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ExternalLink, MessageCircleQuestion, AlertCircle, RefreshCw } from 'lucide-react';
import { useTelegramAuth } from '../hooks/useTelegramAuth';
import { useTelegramTheme } from '../hooks/useTelegramTheme';
import { useTelegramViewport } from '../hooks/useTelegramViewport';
import { api } from '../api/client';
import {
    SubscriptionEditorFields,
    SubscriptionFormData,
    SubscriptionFilters
} from '../components/SubscriptionEditorFields';
import { SubscriptionSummary } from '../components/SubscriptionSummary';
import { Banner, Button, Switch, EmptyState, Spinner } from '../components/ui';
import { validateFilters, getCouplingUpdates, prepareSubscriptionFilters } from '../utils/subscriptionValidation';
import { useHealth } from '../hooks/queries/useHealth';
import { useGeoData } from '../contexts/GeoDataContext';
import { buildAreaLabel } from '../utils/areaLabel';
import { TIER } from '../utils/tier-constants';
import './TelegramSubscriptionPage.css';

const DEFAULT_FILTERS: SubscriptionFilters = {
    minRent: '',
    maxRent: '',
    minBaseRent: '',
    maxBaseRent: '',
    includeUnknowns: true,
    minRooms: '',
    maxRooms: '',
    minSpace: '',
    maxSpace: '',
    wbsFilter: 'ANY',
    wgFilter: 'ANY',
    swapFilter: 'ANY',
    tempFilter: 'ANY',
    providerFilter: 'ANY',
    furnishedFilter: 'ANY',
    closedAreaFilter: 'NONE',
};

const TelegramSubscriptionPage: React.FC = () => {
    const { isAuthenticating, hasAttempted, error: authError, subscription: initialSubscription, user, retry } = useTelegramAuth();
    const { themeParams } = useTelegramTheme();
    useTelegramViewport();

    const [formData, setFormData] = useState<SubscriptionFormData | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isJumping, setIsJumping] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [isBaseRentActive, setIsBaseRentActive] = useState(false);
    const [hasZoneConstraint, setHasZoneConstraint] = useState(false);

    const isEditing = !!initialSubscription?.subscriptionId;
    const saveButtonText = isEditing ? 'Suche speichern' : 'Suche anlegen';

    // Initialize form data when subscription is loaded
    useEffect(() => {
        if (initialSubscription) {
            setFormData({
                zipCodes: initialSubscription.zipCodes || [],
                notificationChannels: initialSubscription.notificationChannels || ['telegram'],
                enabled: initialSubscription.enabled !== false,
                filters: { ...DEFAULT_FILTERS, ...(initialSubscription.filters || {}) },
                uiMetadata: initialSubscription.uiMetadata || { zones: [] }
            });

            if (initialSubscription.filters?.maxBaseRent || initialSubscription.filters?.minBaseRent) {
                setIsBaseRentActive(true);
            }
        } else if (!isAuthenticating && hasAttempted && !authError) {
            // New subscription mode (Onboarding) - only when auth completed without error
            setFormData({
                zipCodes: [],
                notificationChannels: ['telegram'],
                enabled: true,
                filters: { ...DEFAULT_FILTERS },
                uiMetadata: { zones: [] }
            });
        }
    }, [initialSubscription, isAuthenticating, hasAttempted, authError]);

    const handleFieldChange = useCallback((field: string | Partial<SubscriptionFormData>, value?: any) => {
        setFormData(prev => {
            if (!prev) return null;
            if (typeof field === 'string') {
                return { ...prev, [field]: value };
            }
            return { ...prev, ...field };
        });
    }, []);

    const handleFilterChange = useCallback((field: string, value: any) => {
        setFormData(prev => {
            if (!prev) return null;

            const couplingUpdates = getCouplingUpdates(field as any, value, prev.filters);
            return {
                ...prev,
                filters: {
                    ...prev.filters,
                    [field]: value,
                    ...couplingUpdates
                }
            };
        });
    }, []);

    const handleSave = useCallback(async () => {
        if (!formData) return;

        const tg = window.Telegram?.WebApp;
        setIsSaving(true);
        setSaveError(null);
        tg?.MainButton.showProgress(false);

        try {
            const subId = initialSubscription?.subscriptionId;
            let response;

            const payload: any = {
                zipCodes: formData.zipCodes,
                notificationChannels: formData.notificationChannels,
                enabled: formData.enabled,
                uiMetadata: formData.uiMetadata,
                filters: prepareSubscriptionFilters(formData.filters, { isBaseRentActive })
            };

            if (subId) {
                response = await api.put(`/subscription/${subId}`, payload);
            } else {
                response = await api.post('/subscription', payload);
            }

            console.log('Save successful', response.data);
            tg?.HapticFeedback.notificationOccurred('success');

            // Show success briefly and then close
            tg?.MainButton.setParams({
                text: 'Gespeichert!',
                color: '#31b545', // Telegram green
            });

            setTimeout(() => {
                tg?.close();
            }, 1000);

        } catch (err: any) {
            console.error('Save failed', err);
            setSaveError(err.response?.data?.message || err.message || 'Speichern fehlgeschlagen');
            tg?.HapticFeedback.notificationOccurred('error');
            tg?.MainButton.hideProgress();
            tg?.MainButton.setParams({
                text: saveButtonText,
                color: themeParams?.button_color || '#3390ec',
            });
        } finally {
            setIsSaving(false);
        }
    }, [formData, initialSubscription, themeParams, isBaseRentActive, saveButtonText]);

    const handleJumpToDashboard = async () => {
        setIsJumping(true);
        try {
            const response = await api.get('/user/magic-link');
            const magicLink = response.data.magicLink;
            if (magicLink) {
                window.Telegram?.WebApp?.openLink(magicLink);
            }
        } catch (error) {
            console.error('Error jumping to dashboard:', error);
            window.Telegram?.WebApp?.showAlert('Konnte deine Suchen nicht öffnen. Bitte versuche es später.');
        } finally {
            setIsJumping(false);
        }
    };

    // Maintenance Check
    const { data: health } = useHealth();
    // Zone definitions are already loaded by the editor's place search; null until then.
    const { zoneDefinitions } = useGeoData();
    const isMaintenance = health?.maintenanceMode && user?.tier !== TIER.ADMIN;

    // Telegram MainButton Integration
    useEffect(() => {
        const tg = window.Telegram?.WebApp;
        if (!tg) return;

        if (isAuthenticating || !formData) {
            tg.MainButton.hide();
            return;
        }

        if (isMaintenance) {
            tg.MainButton.setText('Wartungsmodus');
            tg.MainButton.disable();
            tg.MainButton.setParams({ color: '#8e8e93' }); // Gray
            tg.MainButton.show();
        } else {
            tg.MainButton.setText(saveButtonText);
            tg.MainButton.onClick(handleSave);
            tg.MainButton.show();

            // Disable button if validation fails or no ZIPs selected
            const validationIssues = validateFilters(formData.filters, { isBaseRentActive });
            const hasErrors = validationIssues.some(i => i.type === 'error');
            const hasZips = formData.zipCodes.length > 0;

            if (hasErrors || !hasZips || isSaving || hasZoneConstraint) {
                tg.MainButton.disable();
                tg.MainButton.setParams({ color: '#8e8e93' }); // Gray out
            } else {
                tg.MainButton.enable();
                tg.MainButton.setParams({ color: themeParams?.button_color || '#3390ec' });
            }
        }

        return () => {
            tg.MainButton.offClick(handleSave);
            tg.MainButton.hide();
        };
    }, [formData, handleSave, isSaving, themeParams, isBaseRentActive, isMaintenance, hasZoneConstraint, saveButtonText, isAuthenticating]);

    // Auto-alert and close when a fresh keyboard token has been sent to chat
    useEffect(() => {
        if (authError === 'TOKEN_EXPIRED_REFRESHED') {
            const tg = window.Telegram?.WebApp;
            if (tg?.showAlert) {
                tg.showAlert(
                    'Dein Zugangs-Link war abgelaufen. Wir haben dir im Chat eine neue Nachricht geschickt!',
                    () => {
                        tg.close();
                    }
                );
            }
        }
    }, [authError]);

    // Validation Issues
    const validationIssues = useMemo(() => {
        if (!formData) return [];
        return validateFilters(formData.filters, { isBaseRentActive });
    }, [formData, isBaseRentActive]);

    if (isAuthenticating) {
        return (
            <div className="telegram-page flex min-h-screen flex-col items-center justify-center gap-4 px-4">
                <Spinner size={40} className="text-brand" />
                <p className="text-sm text-muted">Lade Einstellungen...</p>
            </div>
        );
    }

    if (authError || !formData) {
        if (authError === 'TOKEN_EXPIRED_REFRESHED') {
            return (
                <div className="telegram-page flex min-h-screen items-center justify-center px-4">
                    <EmptyState
                        icon={<RefreshCw className="h-6 w-6 text-brand" />}
                        title="Zugangscode aktualisiert"
                        className="max-w-xs"
                        action={
                            // No retry here: the URL token is still the expired one, so a retry would
                            // only request another refresh message. The fresh button is in the chat.
                            <Button onClick={() => window.Telegram?.WebApp?.close()}>
                                Zum Chat zurückkehren
                            </Button>
                        }
                    >
                        Dein Zugangs-Link war abgelaufen. Wir haben dir im Chat eine neue Nachricht geschickt! Bitte tippe dort nochmals auf ✏️ Suche bearbeiten.
                    </EmptyState>
                </div>
            );
        }

        const isNotFound = authError?.includes('Keine aktive Suche') || !initialSubscription;

        return (
            <div className="telegram-page flex min-h-screen items-center justify-center px-4">
                {isNotFound ? (
                    <EmptyState
                        icon={<MessageCircleQuestion className="h-6 w-6" aria-hidden="true" />}
                        title="Suche nicht gefunden"
                        className="max-w-xs"
                    >
                        Öffne diese Ansicht aus dem Chat deiner Suche: sende /menu und tippe auf ‚Suche bearbeiten‘.
                    </EmptyState>
                ) : (
                    <EmptyState
                        icon={<AlertCircle className="h-6 w-6" aria-hidden="true" />}
                        title="Fehler"
                        className="max-w-xs"
                        action={
                            <div className="flex flex-col gap-2 w-full">
                                <Button onClick={retry}>Erneut versuchen</Button>
                                <Button variant="ghost" onClick={() => window.Telegram?.WebApp?.close()}>
                                    Schließen
                                </Button>
                            </div>
                        }
                    >
                        {authError || 'Daten konnten nicht geladen werden.'}
                    </EmptyState>
                )}
            </div>
        );
    }

    const zipCount = formData.zipCodes?.length || 0;
    const summaryMaxRent = formData.filters.maxRent ? parseInt(formData.filters.maxRent, 10) : null;
    const summaryMinRooms = formData.filters.minRooms ? parseFloat(formData.filters.minRooms) : null;
    const summaryMinArea = formData.filters.minSpace ? parseInt(formData.filters.minSpace, 10) : null;

    return (
        <div
            className="telegram-page px-4 pt-3 pb-[calc(env(safe-area-inset-bottom)+var(--tg-safe-bottom,0px)+16px)]"
            data-density="compact"
        >
            <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted">Deine Suche</p>

            {saveError && (
                <Banner tone="danger" className="mb-4">{saveError}</Banner>
            )}

            {isMaintenance && (
                <Banner tone="warning" className="mb-4" title="Wartungsmodus">
                    Das System wird gerade aktualisiert. Änderungen sind momentan nicht möglich.
                </Banner>
            )}

            <SubscriptionEditorFields
                formData={formData}
                userTier={user?.tier || TIER.FREE}
                isAdmin={!!user?.isAdmin}
                isLoading={isSaving}
                onFieldChange={handleFieldChange}
                onFilterChange={handleFilterChange}
                issues={validationIssues}
                isBaseRentActive={isBaseRentActive}
                onBaseRentToggle={setIsBaseRentActive}
                variant="telegram"
                onConstraintChange={setHasZoneConstraint}
            />

            <p className="mb-3 mt-6 text-xs font-semibold uppercase tracking-wide text-muted">Status</p>
            <Switch
                checked={formData.enabled}
                onCheckedChange={(value) => handleFieldChange('enabled', value)}
                label="Suchauftrag aktiv"
            />

            <div className="mt-6 flex flex-col items-center gap-1 text-center">
                <Button
                    variant="link"
                    leftIcon={<ExternalLink className="h-4 w-4" aria-hidden="true" />}
                    onClick={handleJumpToDashboard}
                    loading={isJumping}
                >
                    Im Browser öffnen
                </Button>
                <p className="text-sm text-muted">Karte, Verlauf und Konto findest du im Browser.</p>
            </div>

            <div className="mt-6">
                <SubscriptionSummary
                    zipCount={zipCount}
                    areaLabel={buildAreaLabel(formData.zipCodes || [], zoneDefinitions)}
                    maxRent={summaryMaxRent}
                    minRooms={summaryMinRooms}
                    minArea={summaryMinArea}
                    channels={formData.notificationChannels}
                    filters={formData.filters}
                />
            </div>
        </div>
    );
};

export default TelegramSubscriptionPage;
